import { useContext, useEffect, useState, type FC } from "react";
import MainLayout from "../components/MainLayout";
import { useParams } from "react-router-dom";
import { LoadingContext } from "../contexts/LoadingContext";
import { errorToast, toolTip } from "../utils/helper-ui";
import { getMerchant, updateMerchant } from "../services/sales/MerchantApi";
import { Merchant } from "../models/sales/merchant";
import { noImage } from "../utils/helper";
import { Divider } from "rsuite";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Moment from "react-moment";
import { CheckIcon } from "@heroicons/react/24/outline";
import { IoClose } from "react-icons/io5";
import { MdStop } from "react-icons/md";
import CoordinatePicker from "../components/coordinate_picker";

// Fix for default marker icon not displaying correctly in React
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

// Example coordinates for the map center

// Inside your component's return statement or where you want to render the map
interface MerchantDetailProps {}

const MapCenterUpdater: React.FC<{ center: [number, number] }> = ({
  center,
}) => {
  const map = useMap();
  map.setView(center);
  map.setZoom(17);
  return null;
};

const MerchantDetail: FC<MerchantDetailProps> = ({}) => {
  const { merchantId } = useParams();
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [merchant, setMerchant] = useState<Merchant>();
  const [position, setPosition] = useState<{ lat: number; lng: number }>({
    lat: merchant?.latitude ?? 0,
    lng: merchant?.longitude ?? 0,
  });
  const [mapCenter, setMapCenter] = useState<[number, number]>([
    merchant?.latitude ?? 0,
    merchant?.longitude ?? 0,
  ]);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    if (!mounted) return;
    if (!merchantId) return;
    getDetail();
  }, [merchantId, mounted]);

  const getDetail = async () => {
    try {
      setIsLoading(true);
      let resp = await getMerchant(merchantId!);
      let respJson = await resp.json();
      //   console.log(respJson);
      setMerchant(respJson.data);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (merchant && merchant.latitude && merchant.longitude) {
      setPosition({ lat: merchant.latitude!, lng: merchant.longitude! });
      setMapCenter([merchant.latitude!, merchant.longitude!]);
    }
  }, [merchant]);

  const updateStatus = async (status: string) => {
    try {
      if (
        !window.confirm(`Are you sure you want to update status to ${status}?`)
      )
        return;
      setIsLoading(true);
      let resp = await updateMerchant(merchantId!, {
        ...merchant,
        status,
      });
      let respJson = await resp.json();
      //   console.log(respJson);
      getDetail();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeader = () => {
    if (merchant?.status == "SUSPENDED") {
      return (
        <div className="flex flex-row justify-end items-center px-2 gap-2">
          <button
            className="bg-green-500 hover:bg-green-700 text-white flex p-2 justify-center items-center font-bold aspect-square text-center rounded-full "
            onClick={() => {
              updateStatus("ACTIVE");
              //TODO: implement reject function
            }}
          >
            {toolTip(
              "Reactive",
              <CheckIcon className="w-5 h-5" />,
              "bottomEnd"
            )}
          </button>
        </div>
      );
    }
    if (merchant?.status == "ACTIVE") {
      return (
        <div className="flex flex-row justify-end items-center px-2 gap-2">
          <button
            className="bg-red-500 hover:bg-red-700 text-white flex p-2 justify-center items-center font-bold aspect-square text-center rounded-full "
            onClick={() => {
              updateStatus("SUSPENDED");
              //TODO: implement reject function
            }}
          >
            {toolTip("Suspend", <MdStop className="w-5 h-5" />, "bottomEnd")}
          </button>
        </div>
      );
    }
    if (merchant?.status == "PENDING") {
      return (
        <div className="flex flex-row justify-end items-center px-2 gap-2">
          {merchant?.user?.verified_at && (
            <button
              className="bg-green-500 hover:bg-green-700 text-white flex p-2 justify-center items-center font-bold aspect-square text-center rounded-full"
              onClick={() => {
                //TODO: implement approve function
                updateStatus("ACTIVE");
              }}
            >
              {toolTip(
                "Approve",
                <CheckIcon className="w-5 h-5" />,
                "bottomEnd"
              )}
            </button>
          )}
          <button
            className="bg-red-500 hover:bg-red-700 text-white flex p-2 justify-center items-center font-bold aspect-square text-center rounded-full "
            onClick={() => {
              updateStatus("REJECTED");
              //TODO: implement reject function
            }}
          >
            {toolTip("Reject", <IoClose className="w-5 h-5" />, "bottomEnd")}
          </button>
        </div>
      );
    }
    return null;
  };
  return (
    <MainLayout header={renderHeader()}>
      {merchant && (
        <div className="container mx-auto p-4">
          <div className="flex flex-wrap -mx-4">
            <div className="w-full lg:w-1/4 xl:w-1/4 p-4">
              <img
                src={merchant.picture?.url ?? "/images/logo-toko21.png"}
                onError={noImage}
                alt={merchant.name}
                className="object-contain h-48 w-full"
              />
            </div>
            <div className="w-full lg:w-3/4 xl:w-3/4 p-4">
              <h2 className="text-2xl font-bold mb-2">{merchant.name}</h2>
              <div className="text-normal mb-2">
                <div className="font-bold w-64">Address:</div>{" "}
                {merchant.address}
              </div>
              <div className="text-normal mb-2">
                <div className="font-bold w-64">Phone:</div>{" "}
                {merchant.phone.length ? merchant.phone : "-"}
              </div>

              <div className="text-normal mb-2">
                <div className="font-bold w-64">Status:</div>{" "}
                {merchant.status === "ACTIVE" && (
                  <div className="w-48 text-sm bg-green-500 text-white rounded px-2 py-1 text-center">
                    {merchant.status}
                  </div>
                )}
                {merchant.status === "PENDING" && (
                  <div className="w-48 text-sm bg-orange-500 text-white rounded px-2 py-1 text-center">
                    {merchant.status}
                  </div>
                )}
                {merchant.status === "SUSPENDED" && (
                  <div className="w-48 text-sm bg-red-500 text-white rounded px-2 py-1 text-center">
                    {merchant.status}
                  </div>
                )}
              </div>
              <CoordinatePicker
                hideSearch
                dragging={false}
                lat={merchant.latitude}
                lng={merchant.longitude}
                onPicker={(p) => {
                  // setMerchant({
                  //   ...merchant,
                  //   latitude: p.lat,
                  //   longitude: p.lng,
                  // })
                }
                
                }
              />

              <Divider />
              <div className="grid grid-cols-2 gap-2 mt-8">
                <div>
                  <h3 className="text-xl font-bold mb-2 text-red-400">
                    Company Information
                  </h3>
                  <div className="text-normal mb-2">
                    <div className="font-bold w-64">Company Name:</div>{" "}
                    {merchant.company?.name}
                  </div>
                  <div className="text-normal mb-2">
                    <div className="font-bold w-64">Company Address:</div>{" "}
                    {merchant.company?.address}
                  </div>
                  <div className="text-normal mb-2">
                    <div className="font-bold w-64">Company Phone:</div>{" "}
                    {merchant.company?.phone}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-red-400">
                    User Information
                  </h3>
                  <div className="text-normal mb-2">
                    <div className="font-bold w-64">User Name:</div>{" "}
                    {merchant.user?.full_name}
                  </div>
                  <div className="text-normal mb-2">
                    <div className="font-bold w-64">User Email:</div>{" "}
                    {merchant.user?.email}
                  </div>
                  <div className="text-normal mb-2">
                    <div className="font-bold w-64">User Phone:</div>{" "}
                    {merchant.user?.phone_number}
                  </div>
                  <div className="text-normal mb-2">
                    <div className="font-bold w-64">User Verified:</div>{" "}
                    {merchant.user?.verified_at && (
                      <Moment format="DD MMM YYYY HH:mm">
                        {merchant.user?.verified_at}
                      </Moment>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};
export default MerchantDetail;
